import React, { useState, useEffect, useRef } from "react";
import {
    Play,
    Pause,
    Volume2,
    VolumeX,
    Heart,
    Plus,
    Share2,
    X,
    Link,
    Twitter,
    Facebook,
    Instagram,
    ChevronDown,
} from "lucide-react";
import styles from "./HowlerPlayer.module.css";
import {
    useCheckContentInPlaylist,
    useCreatePlaylist,
    useDeletePlaylist,
} from "../../../hooks/usePlayListHooks";
import useAuthStore from "../../../store/authStore";
import {
    useLikeItem,
    useUnlikeItem,
    useLikeStatus,
} from "../../../hooks/useLikesHooks";
import { useAudioPlayer } from "../AudioPlayerContext";

const HowlerPlayer = ({ audio, isPlaying, setIsPlaying, onClose }) => {
    const [volume, setVolume] = useState(0.8);
    const [isMuted, setIsMuted] = useState(false);
    const [duration, setDuration] = useState(0);
    const [seek, setSeek] = useState(0);
    const [isDragging, setIsDragging] = useState(false);
    const [showShareMenu, setShowShareMenu] = useState(false);
    const [copySuccess, setCopySuccess] = useState(false);
    const [playbackRate, setPlaybackRate] = useState(1);
    const [isFullView, setIsFullView] = useState(false);
    const [audioReady, setAudioReady] = useState(false);

    const { isAuthenticated } = useAuthStore();
    const { audioRef } = useAudioPlayer(); // Get audioRef from context

    const rafRef = useRef(null);
    const progressRef = useRef(null);
    const wasPlayingRef = useRef(false);
    const shareMenuRef = useRef(null);
    const audioIdRef = useRef(null);
    const currentSeekRef = useRef(0);

    // Custom hooks for playlist operations
    const createPlaylistMutation = useCreatePlaylist();
    const deletePlaylistMutation = useDeletePlaylist();
    const { data: playlistStatus } = useCheckContentInPlaylist(
        "audio",
        audio?.audioId
    );

    const { data: likeStatus } = useLikeStatus("audio", audio?.audioId);
    const likeItemMutation = useLikeItem();
    const unlikeItemMutation = useUnlikeItem();

    const isInPlaylist = playlistStatus?.exists || false;

    // Reset player when audio changes
    useEffect(() => {
        if (audio?.id !== audioIdRef.current) {
            audioIdRef.current = audio?.id;

            // Reset all state
            setSeek(0);
            currentSeekRef.current = 0;
            setDuration(0);
            setAudioReady(false);
            setIsDragging(false);
            // Let context handle play state
        }
    }, [audio]);


    // Initialize audio element
    useEffect(() => {
        const audioElement = audioRef.current;
        if (!audioElement) return;

        // Set up audio element properties
        audioElement.volume = isMuted ? 0 : volume;
        audioElement.playbackRate = playbackRate;

        const handleLoadedMetadata = () => {
            setDuration(audioElement.duration);
            setAudioReady(true);
        };

        const handleTimeUpdate = () => {
            if (!isDragging) {
                const currentTime = audioElement.currentTime;
                setSeek(currentTime);
                currentSeekRef.current = currentTime;
            }
        };

        const handlePlay = () => {
            setIsPlaying(true);
        };

        const handlePause = () => {
            setIsPlaying(false);
        };

        const handleEnded = () => {
            setIsPlaying(false);
            setSeek(0);
            currentSeekRef.current = 0;
            audioElement.currentTime = 0;
        };

        const handleError = (e) => {
            console.error("Audio error:", e);
            setIsPlaying(false);
            setAudioReady(false);
        };

        const handleCanPlay = () => {
            setAudioReady(true);
        };

        // Add event listeners
        audioElement.addEventListener("loadedmetadata", handleLoadedMetadata);
        audioElement.addEventListener("timeupdate", handleTimeUpdate);
        audioElement.addEventListener("play", handlePlay);
        audioElement.addEventListener("pause", handlePause);
        audioElement.addEventListener("ended", handleEnded);
        audioElement.addEventListener("error", handleError);
        audioElement.addEventListener("canplay", handleCanPlay);


        // Initial state sync
        if (audioElement.duration) {
            setDuration(audioElement.duration);
            setAudioReady(true);
        }
        setSeek(audioElement.currentTime);
        currentSeekRef.current = audioElement.currentTime;


        return () => {
            // Cleanup event listeners
            audioElement.removeEventListener("loadedmetadata", handleLoadedMetadata);
            audioElement.removeEventListener("timeupdate", handleTimeUpdate);
            audioElement.removeEventListener("play", handlePlay);
            audioElement.removeEventListener("pause", handlePause);
            audioElement.removeEventListener("ended", handleEnded);
            audioElement.removeEventListener("error", handleError);
            audioElement.removeEventListener("canplay", handleCanPlay);
        };
    }, [audio, isDragging, volume, isMuted, playbackRate, audioRef, setIsPlaying]);

    // Handle play/pause with proper user interaction
    const togglePlay = () => {
        setIsPlaying(!isPlaying);
    };

    // Handle volume change
    const handleVolumeChange = (e) => {
        const newVolume = parseFloat(e.target.value);
        setVolume(newVolume);
        if (audioRef.current) {
            audioRef.current.volume = newVolume;
        }
        if (newVolume === 0) {
            setIsMuted(true);
        } else if (isMuted) {
            setIsMuted(false);
        }
    };

    // Handle mute/unmute
    const toggleMute = () => {
        const newMuted = !isMuted;
        setIsMuted(newMuted);
        if (audioRef.current) {
            audioRef.current.volume = newMuted ? 0 : volume;
        }
    };

    // Handle seeking
    const handleSeekChange = (e) => {
        const newSeek = parseFloat(e.target.value);
        setSeek(newSeek);
    };

    const handleSeekStart = () => {
        setIsDragging(true);
        wasPlayingRef.current = isPlaying;
    };

    const handleSeekEnd = () => {
        setIsDragging(false);
        if (audioRef.current) {
            audioRef.current.currentTime = seek;
            currentSeekRef.current = seek;
        }
    };

    // Skip forward/backward
    const skipForward = () => {
        if (audioRef.current) {
            const newTime = Math.min(audioRef.current.currentTime + 10, duration);
            audioRef.current.currentTime = newTime;
            setSeek(newTime);
            currentSeekRef.current = newTime;
        }
    };

    const skipBackward = () => {
        if (audioRef.current) {
            const newTime = Math.max(audioRef.current.currentTime - 10, 0);
            audioRef.current.currentTime = newTime;
            setSeek(newTime);
            currentSeekRef.current = newTime;
        }
    };

    // Change playback rate
    const handlePlaybackRateChange = () => {
        let newRate;
        if (playbackRate === 1) newRate = 1.25;
        else if (playbackRate === 1.25) newRate = 1.5;
        else if (playbackRate === 1.5) newRate = 2;
        else if (playbackRate === 2) newRate = 0.75;
        else newRate = 1;

        setPlaybackRate(newRate);
        if (audioRef.current) {
            audioRef.current.playbackRate = newRate;
        }
    };

    // Toggle full view mode
    const toggleFullView = () => {
        setIsFullView(!isFullView);
    };

    // Toggle like
    const toggleLike = () => {
        if (likeStatus?.liked) {
            unlikeItemMutation.mutate({
                item_type: "audio",
                item_id: audio.audioId,
            });
        } else {
            likeItemMutation.mutate({
                item_type: "audio",
                item_id: audio.audioId,
            });
        }
    };

    // Handle adding/removing from playlist
    const handlePlaylistAction = () => {
        if (isInPlaylist) {
            deletePlaylistMutation.mutate({
                item_type: "audio",
                content_id: audio.id,
            });
        } else {
            createPlaylistMutation.mutate({
                item_type: "audio",
                content_id: audio.audioId,
                title: audio.title,
                description:
                    audio.description ||
                    `Playlist featuring ${audio.title} by ${audio.author}`,
            });
        }
    };

    // Toggle share menu
    const toggleShareMenu = () => {
        setShowShareMenu(!showShareMenu);
    };

    // Copy link to clipboard
    const copyToClipboard = () => {
        const slug = audio?.slug || window.location.pathname.split("/").pop();
        const audioLink = `${window.location.origin}/audio-play/${slug}`;

        if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard
                .writeText(audioLink)
                .then(() => {
                    setCopySuccess(true);
                    setTimeout(() => setCopySuccess(false), 2000);
                })
                .catch((err) => {
                    console.error("Failed to copy: ", err);
                    fallbackCopyToClipboard(audioLink);
                });
        } else {
            fallbackCopyToClipboard(audioLink);
        }

        setTimeout(() => setShowShareMenu(false), 2000);
    };

    // Fallback method using a temporary input element
    const fallbackCopyToClipboard = (text) => {
        const textArea = document.createElement("textarea");
        textArea.value = text;
        textArea.style.position = "fixed";
        textArea.style.left = "-999999px";
        textArea.style.top = "-999999px";
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();

        try {
            const successful = document.execCommand("copy");
            setCopySuccess(successful);
            if (successful) {
                setTimeout(() => setCopySuccess(false), 2000);
            }
        } catch (err) {
            console.error("Fallback: Oops, unable to copy", err);
        }

        document.body.removeChild(textArea);
    };

    // Share to social media platforms
    const shareToSocial = (platform) => {
        const shareUrl = audio.song_url || window.location.href;
        const shareTitle = `Listen to ${audio.title} by ${audio.author}`;
        let url;

        switch (platform) {
            case "twitter":
                url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(
                    shareTitle
                )}&url=${encodeURIComponent(shareUrl)}`;
                break;
            case "whatsapp":
                url = `https://wa.me/?text=${encodeURIComponent(
                    shareTitle + " " + shareUrl
                )}`;
                break;
            case "facebook":
                url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
                    shareUrl
                )}`;
                break;
            case "instagram":
                navigator.clipboard.writeText(shareUrl);
                setCopySuccess(true);
                setTimeout(() => setCopySuccess(false), 2000);
                setTimeout(() => setShowShareMenu(false), 2000);
                return;
            default:
                return;
        }

        window.open(url, "_blank", "width=600,height=400");
        setShowShareMenu(false);
    };

    // Close menus when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                shareMenuRef.current &&
                !shareMenuRef.current.contains(event.target)
            ) {
                setShowShareMenu(false);
            }
        };

        if (showShareMenu) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [showShareMenu]);

    // Clean up on unmount
    useEffect(() => {
        return () => {
            if (rafRef.current) {
                cancelAnimationFrame(rafRef.current);
            }
        };
    }, []);

    // Format time (seconds to MM:SS)
    const formatTime = (seconds) => {
        if (!seconds || isNaN(seconds)) return "0:00";
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${mins}:${secs < 10 ? "0" : ""}${secs}`;
    };

    // Get episode title and details
    const episodeInfo = audio.title ? audio.title.split(":") : ["", ""];
    const episodeNumber = episodeInfo[0]?.trim() || "";
    const episodeTitle =
        episodeInfo[1]?.trim() || audio.title || "Untitled Episode";

    // Calculate progress for the seek bar (as a percentage)
    const progressPercentage = duration > 0 ? (seek / duration) * 100 : 0;
    const seekBarStyle = {
        "--seekPercent": `${progressPercentage}%`,
    };

    return (
        <div
            className={`${styles.audioPlayer} ${isFullView ? styles.fullView : ""}`}
            style={seekBarStyle}
        >
            <div className={styles.playerContainer}>
                {/* The <audio> element is now managed by the context provider */}

                {/* Rest of your JSX remains exactly the same */}
                {isFullView ? (
                    <div className={styles.fullViewContainer}>
                        <div className={styles.fullViewHeader}>
                            <button
                                className={styles.closeFullViewButton}
                                onClick={toggleFullView}
                                aria-label="Minimize player"
                            >
                                <ChevronDown size={24} />
                            </button>
                        </div>

                        <div className={styles.fullViewContent}>
                            <div className={styles.fullViewImageContainer}>
                                <img
                                    src={
                                        audio.image ||
                                        audio.mobile_image_url ||
                                        audio.computer_image_url ||
                                        "/api/placeholder/400/400"
                                    }
                                    alt={audio.title}
                                    className={styles.fullViewCoverImage}
                                />
                                {!isPlaying && (
                                    <div
                                        className={styles.fullViewPlayOverlay}
                                        onClick={togglePlay}
                                    >
                                        <Play size={48} />
                                    </div>
                                )}
                            </div>

                            <div className={styles.fullViewInfo}>
                                <h2 className={styles.fullViewTitle}>{episodeTitle}</h2>
                            </div>

                            <div className={styles.fullViewControlsWrapper}>
                                <div className={styles.fullViewSeekContainer}>
                                    <input
                                        type="range"
                                        min="0"
                                        max={duration || 100}
                                        value={seek}
                                        step="any"
                                        className={styles.fullViewSeekBar}
                                        onChange={handleSeekChange}
                                        onMouseDown={handleSeekStart}
                                        onMouseUp={handleSeekEnd}
                                        onTouchStart={handleSeekStart}
                                        onTouchEnd={handleSeekEnd}
                                        aria-label="Seek"
                                        style={seekBarStyle}
                                    />
                                    <div className={styles.fullViewTimeDisplay}>
                                        <span>{formatTime(seek)}</span>
                                        <span>{formatTime(duration)}</span>
                                    </div>
                                </div>

                                <div className={styles.fullViewActionRow}>
                                    <div className={styles.fullViewLeftActions}>
                                        {isAuthenticated && (
                                            <>
                                                <button
                                                    className={styles.fullViewActionBtn}
                                                    onClick={toggleLike}
                                                    aria-label={likeStatus?.liked ? "Unlike" : "Like"}
                                                >
                                                    <Heart
                                                        size={24}
                                                        fill={likeStatus?.liked ? "#ff3333" : "none"}
                                                        stroke={
                                                            likeStatus?.liked ? "#ff3333" : "currentColor"
                                                        }
                                                    />
                                                </button>

                                                {!playlistStatus?.inPlaylist && (
                                                    <button
                                                        className={styles.fullViewActionBtn}
                                                        onClick={handlePlaylistAction}
                                                        aria-label="Add to playlist"
                                                    >
                                                        <Plus size={24} />
                                                    </button>
                                                )}
                                            </>
                                        )}

                                        <div className={styles.shareContainer} ref={shareMenuRef}>
                                            <button
                                                className={styles.fullViewActionBtn}
                                                onClick={toggleShareMenu}
                                                aria-label="Share"
                                            >
                                                <Share2 size={24} />
                                            </button>

                                            {showShareMenu && (
                                                <div className={styles.shareMenu}>
                                                    <button
                                                        className={styles.shareOption}
                                                        onClick={copyToClipboard}
                                                        aria-label="Copy link"
                                                    >
                                                        <Link size={16} />
                                                        <span>{copySuccess ? "Copied!" : "Copy link"}</span>
                                                    </button>
                                                    <button
                                                        className={styles.shareOption}
                                                        onClick={() => shareToSocial("twitter")}
                                                        aria-label="Share on Twitter"
                                                    >
                                                        <Twitter size={16} />
                                                        <span>Twitter</span>
                                                    </button>
                                                    <button
                                                        className={styles.shareOption}
                                                        onClick={() => shareToSocial("facebook")}
                                                        aria-label="Share on Facebook"
                                                    >
                                                        <Facebook size={16} />
                                                        <span>Facebook</span>
                                                    </button>
                                                    <button
                                                        className={styles.shareOption}
                                                        onClick={() => shareToSocial("instagram")}
                                                        aria-label="Share on Instagram"
                                                    >
                                                        <Instagram size={16} />
                                                        <span>Instagram</span>
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <div className={styles.fullViewRightActions}>
                                        <button
                                            className={styles.fullViewActionBtn}
                                            onClick={handlePlaybackRateChange}
                                            aria-label="Change playback speed"
                                        >
                                            {playbackRate}x
                                        </button>

                                        <div className={styles.fullViewVolumeControl}>
                                            <button
                                                onClick={toggleMute}
                                                aria-label={isMuted ? "Unmute" : "Mute"}
                                                className={styles.fullViewVolumeBtn}
                                            >
                                                {isMuted ? (
                                                    <VolumeX size={24} />
                                                ) : (
                                                    <Volume2 size={24} />
                                                )}
                                            </button>
                                            <input
                                                type="range"
                                                min="0"
                                                max="1"
                                                step="0.01"
                                                value={isMuted ? 0 : volume}
                                                onChange={handleVolumeChange}
                                                className={styles.fullViewVolumeSlider}
                                                aria-label="Volume"
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className={styles.fullViewControls}>
                                    <button
                                        className={styles.fullViewControlBtn}
                                        onClick={skipBackward}
                                        aria-label="Skip backward 10 seconds"
                                    >
                                        <img
                                            src="/backward.png"
                                            alt="Skip backward"
                                            width={28}
                                            height={28}
                                        />
                                    </button>

                                    <button
                                        className={styles.fullViewPlayPauseBtn}
                                        onClick={togglePlay}
                                        aria-label={isPlaying ? "Pause" : "Play"}
                                    >
                                        {isPlaying ? <Pause size={36} /> : <Play size={36} />}
                                    </button>
                                    <button
                                        className={styles.fullViewControlBtn}
                                        onClick={skipForward}
                                        aria-label="Skip forward 10 seconds"
                                    >
                                        <img
                                            src="/forward.png"
                                            alt="Skip forward"
                                            width={28}
                                            height={28}
                                        />
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className={styles.playerTopRow}>
                        {/* Left section: Episode info */}
                        <div className={styles.episodeInfo} onClick={toggleFullView}>
                            <img
                                src={
                                    audio.image ||
                                    audio.mobile_image_url ||
                                    audio.computer_image_url ||
                                    "/api/placeholder/80/80"
                                }
                                alt={audio.title}
                                className={styles.coverImage}
                            />
                            <div className={styles.titleContainer}>
                                <div className={styles.episodeNumber}>
                                    {episodeNumber || "Episode"}
                                </div>
                                <div className={styles.episodeTitle}>{episodeTitle}</div>
                            </div>
                        </div>

                        {/* Control section aligned to center */}
                        <div className={styles.gridContainer}>
                            <div>
                                <div className={styles.centerControls}>
                                    <button
                                        className={styles.fullViewControlBtn}
                                        onClick={skipBackward}
                                        aria-label="Skip backward 10 seconds"
                                    >
                                        <img
                                            src="/backward.png"
                                            alt="Skip backward"
                                            width={28}
                                            height={28}
                                        />
                                    </button>

                                    <button
                                        className={styles.playPauseBtn}
                                        onClick={togglePlay}
                                        aria-label={isPlaying ? "Pause" : "Play"}
                                    >
                                        {isPlaying ? <Pause size={24} /> : <Play size={24} />}
                                    </button>
                                    <button
                                        className={styles.fullViewControlBtn}
                                        onClick={skipForward}
                                        aria-label="Skip forward 10 seconds"
                                    >
                                        <img
                                            src="/forward.png"
                                            alt="Skip forward"
                                            width={28}
                                            height={28}
                                        />
                                    </button>
                                </div>
                            </div>

                            <div>
                                {/* Progress bar section */}
                                <div className={styles.seekContainer}>
                                    <span className={styles.timeDisplay}>{formatTime(seek)}</span>
                                    <div className={styles.progressWrapper}>
                                        <input
                                            type="range"
                                            min="0"
                                            max={duration || 100}
                                            value={seek}
                                            step="any"
                                            className={styles.seekBar}
                                            onChange={handleSeekChange}
                                            onMouseDown={handleSeekStart}
                                            onMouseUp={handleSeekEnd}
                                            onTouchStart={handleSeekStart}
                                            onTouchEnd={handleSeekEnd}
                                            ref={progressRef}
                                            aria-label="Seek"
                                            style={seekBarStyle}
                                        />
                                    </div>
                                    <span className={styles.timeDisplay}>
                                        {formatTime(duration)}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Right section for playback speed */}
                        <div className={styles.rightControls}>
                            <button
                                className={styles.rateButton}
                                onClick={handlePlaybackRateChange}
                                aria-label="Change playback speed"
                            >
                                {playbackRate}x
                            </button>

                            {/* Share button */}
                            <div className={styles.shareContainer} ref={shareMenuRef}>
                                <button
                                    className={`${styles.controlBtn} ${styles.shareBtn}`}
                                    onClick={toggleShareMenu}
                                    aria-label="Share"
                                >
                                    <Share2 size={20} />
                                </button>

                                {showShareMenu && (
                                    <div className={styles.shareMenu}>
                                        <button
                                            className={styles.shareOption}
                                            onClick={copyToClipboard}
                                            aria-label="Copy link"
                                        >
                                            <Link size={16} />
                                            <span>{copySuccess ? "Copied!" : "Copy link"}</span>
                                        </button>
                                        <button
                                            className={styles.shareOption}
                                            onClick={() => shareToSocial("twitter")}
                                            aria-label="Share on Twitter"
                                        >
                                            <Twitter size={16} />
                                            <span>Twitter</span>
                                        </button>
                                        <button
                                            className={styles.shareOption}
                                            onClick={() => shareToSocial("facebook")}
                                            aria-label="Share on Facebook"
                                        >
                                            <Facebook size={16} />
                                            <span>Facebook</span>
                                        </button>
                                        <button
                                            className={styles.shareOption}
                                            onClick={() => shareToSocial("instagram")}
                                            aria-label="Share on Instagram"
                                        >
                                            <Instagram size={16} />
                                            <span>Instagram</span>
                                        </button>
                                    </div>
                                )}
                            </div>

                            {/* Only show heart and plus buttons when user is authenticated */}
                            {isAuthenticated && (
                                <>
                                    {/* Heart button */}
                                    <button
                                        className={`${styles.controlBtn} ${styles.darkMode}`}
                                        onClick={toggleLike}
                                        aria-label={likeStatus?.liked ? "Unlike" : "Like"}
                                    >
                                        <Heart
                                            size={20}
                                            fill={likeStatus?.liked ? "#ff3333" : "none"}
                                            stroke={likeStatus?.liked ? "#ff3333" : "currentColor"}
                                        />
                                    </button>

                                    {/* Plus button */}
                                    {playlistStatus?.inPlaylist ? null : (
                                        <button
                                            className={`${styles.controlBtn} ${styles.darkMode}`}
                                            onClick={handlePlaylistAction}
                                            aria-label="Add to playlist"
                                        >
                                            <Plus size={20} />
                                        </button>
                                    )}
                                </>
                            )}

                            {/* Volume control */}
                            <button
                                className={styles.volumeButton}
                                onClick={toggleMute}
                                aria-label={isMuted ? "Unmute" : "Mute"}
                            >
                                {isMuted ? <VolumeX size={20} /> : <Volume2 size={20} />}
                            </button>

                            {/* Close button */}
                            <button
                                className={styles.closeButton}
                                onClick={onClose}
                                aria-label="Close player"
                            >
                                <X size={20} />
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default HowlerPlayer;